import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_LANGUAGE,
  LANGUAGE_STORAGE_KEY,
  Language,
  TranslationKey,
  TranslationNamespace,
  translations,
} from './translations';

interface I18nContextValue {
  language: Language;
  setLanguage: (language: Language) => void;
  translate: <TNamespace extends TranslationNamespace>(
    namespace: TNamespace,
    key: TranslationKey<TNamespace>
  ) => string;
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

const isSupportedLanguage = (language: string | null): language is Language => {
  return !!language && language in translations;
};

const getInitialLanguage = (): Language => {
  if (typeof window === 'undefined') {
    return DEFAULT_LANGUAGE;
  }

  const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return isSupportedLanguage(storedLanguage) ? storedLanguage : DEFAULT_LANGUAGE;
};

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>(getInitialLanguage);

  useEffect(() => {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  }, [language]);

  const value = useMemo<I18nContextValue>(() => {
    return {
      language,
      setLanguage,
      translate: (namespace, key) => String(translations[language][namespace][key]),
    };
  }, [language]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);

  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }

  return context;
}

export function useTranslation<TNamespace extends TranslationNamespace>(namespace: TNamespace) {
  const { language, setLanguage, translate } = useI18n();
  const t = useCallback(
    (key: TranslationKey<TNamespace>) => translate(namespace, key),
    [namespace, translate]
  );

  return {
    language,
    setLanguage,
    t,
  };
}
